import React, { useState, useEffect } from 'react';
import '../MarsTheme.css';

const MissionSchedule = ({ availability }) => {
    const [busySlots, setBusySlots] = useState([]);
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedTime, setSelectedTime] = useState(null);

    // Booking Form State
    const [formData, setFormData] = useState({ name: '', topic: '' });

    // 1. Fetch Busy Slots (Keep this to show what's taken)
    useEffect(() => {
        if (availability.availabilityApiUrl) {
            fetch(availability.availabilityApiUrl)
                .then(res => res.json())
                .then(data => {
                    if (data.busySlots) setBusySlots(data.busySlots.map(s => ({ start: new Date(s.start), end: new Date(s.end) })));
                })
                .catch(err => console.error("Comms Error:", err));
        }
    }, [availability.availabilityApiUrl]);

    // Calendar Logic
    const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
    const firstDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay();

    // Limit: Max 2 months in future
    const maxDate = new Date();
    maxDate.setMonth(maxDate.getMonth() + 2);

    const handleDayClick = (day) => {
        const clickedDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // Block past dates AND dates beyond 2 months
        if (clickedDate < today || clickedDate > maxDate) return;

        setSelectedDate(clickedDate);
    };

    const handleSlotClick = (time) => {
        setSelectedTime(time);
        setModalOpen(true);
    };

    const checkAvailability = (hour) => {
        if (!selectedDate) return false;
        const slotStart = new Date(selectedDate);
        slotStart.setHours(hour, 0, 0);
        const slotEnd = new Date(slotStart);
        slotEnd.setHours(hour + 1, 0, 0);

        return !busySlots.some(busy =>
            (slotStart >= busy.start && slotStart < busy.end) ||
            (slotEnd > busy.start && slotEnd <= busy.end) ||
            (slotStart <= busy.start && slotEnd >= busy.end)
        );
    };

    const handleMailto = (e) => {
        e.preventDefault();

        const dateStr = selectedDate.toLocaleDateString();
        const timeStr = selectedTime < 10 ? `0${selectedTime}:00` : `${selectedTime}:00`;

        const subject = encodeURIComponent(`Mission Request: ${formData.topic}`);
        const body = encodeURIComponent(
            `Hi Himanshu,\n\n` +
            `I would like to schedule a meeting.\n\n` +
            `Name: ${formData.name}\n` +
            `Proposed Time: ${dateStr} at ${timeStr}\n` +
            `Topic: ${formData.topic}\n\n` +
            `Awaiting your confirmation.\n`
        );

        // Open Mail Client
        window.location.href = `mailto:himanshuvishwas58@gmail.com?subject=${subject}&body=${body}`;

        // Close modal after short delay
        setTimeout(() => {
            setModalOpen(false);
            setFormData({ name: '', topic: '' });
            setSelectedDate(null);
        }, 1000);
    };

    return (
        <div className="mission-schedule-container glass-panel fade-in">
            <div className="calendar-wrapper">
                <div className="calendar-view">
                    <h3 className="widget-title">Mission Schedule Protocol</h3>
                    <div className="month-nav">
                        <button onClick={() => setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() - 1)))}>&lt;</button>
                        <span>{months[currentDate.getMonth()]} {currentDate.getFullYear()}</span>
                        <button onClick={() => {
                            // Optional: Prevent navigating months too far? 
                            // User didn't strictly ask to block month nav, just inactive dates.
                            setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() + 1)))
                        }}>&gt;</button>
                    </div>
                    <div className="calendar-grid">
                        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(d => <div key={d} className="day-header">{d}</div>)}
                        {[...Array(firstDay)].map((_, i) => <div key={`empty-${i}`} className="day-cell empty"></div>)}
                        {[...Array(daysInMonth)].map((_, i) => {
                            const day = i + 1;
                            const dateObj = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);

                            const isPast = dateObj < today;
                            const isTooFar = dateObj > maxDate;
                            const isSelected = selectedDate && selectedDate.getDate() === day && selectedDate.getMonth() === currentDate.getMonth();

                            return (
                                <div
                                    key={day}
                                    className={`day-cell ${isPast || isTooFar ? 'disabled' : ''} ${isSelected ? 'selected' : ''}`}
                                    onClick={() => !isPast && !isTooFar && handleDayClick(day)}
                                >
                                    {day}
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="slots-view">
                    {!selectedDate ? (
                        <div className="placeholder-msg">Select a date to scan for open channels.</div>
                    ) : (
                        <>
                            <h4>Available Channels for {selectedDate.toLocaleDateString()}</h4>
                            <div className="slots-grid">
                                {[...Array(15)].map((_, i) => { // 8-22
                                    const hour = i + 8;
                                    const isAvailable = checkAvailability(hour);
                                    const timeLabel = `${hour > 12 ? hour - 12 : hour} ${hour >= 12 ? 'PM' : 'AM'}`;

                                    return (
                                        <button
                                            key={hour}
                                            className={`slot-btn ${isAvailable ? 'open' : 'busy'}`}
                                            disabled={!isAvailable}
                                            onClick={() => handleSlotClick(hour)}
                                        >
                                            {timeLabel}
                                        </button>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Simplified Booking Modal */}
            {modalOpen && (
                <div className="booking-modal-overlay">
                    <div className="glass-panel booking-modal">
                        <form onSubmit={handleMailto}>
                            <h3>Request Secure Channel</h3>
                            <p>Requesting: <strong>{selectedDate.toLocaleDateString()}</strong> at <strong>{selectedTime}:00</strong></p>

                            <div className="form-group">
                                <label>Identity (Name)</label>
                                <input required type="text" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} />
                            </div>
                            <div className="form-group">
                                <label>Mission Objective (Topic)</label>
                                <textarea required rows="3" value={formData.topic} onChange={e => setFormData({ ...formData, topic: e.target.value })}></textarea>
                            </div>

                            <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                                * This will open your email client to send the request directly to the Commander.
                            </p>

                            <div className="modal-actions">
                                <button type="button" className="btn-secondary" onClick={() => setModalOpen(false)}>Abort</button>
                                <button type="submit" className="btn-primary">Proceed to Comms</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

        </div>
    );
};

export default MissionSchedule;
